import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Auth } from './components/Auth';
import { ContactForm } from './components/ContactForm';
import { AccountSettings } from './components/AccountSettings';
import { Products } from './components/Products';
import { ProductDetail } from './components/ProductDetail';
import { TermsAndConditions } from './components/TermsAndConditions';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { PrivacyCenter } from './components/PrivacyCenter';
import { CommunicationPreferences } from './components/CommunicationPreferences';
import { DataCollectionManagement } from './components/DataCollectionManagement';
import { PrivacyRightsPage } from './components/PrivacyRightsPage';
import { CookiesManagement } from './components/CookiesManagement';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { NotificationBanner } from './components/NotificationBanner';
import { RouteChangeTracker } from './components/RouteChangeTracker';
import { useOneTrust } from './hooks/useOneTrust';
import { supabase } from './lib/supabase';
import { Home } from './components/Home';
import { Resources } from './components/Resources';
import { FeatureRequest } from './components/FeatureRequest';
import { AdminLayout } from './components/AdminLayout';
import { AdminFeatureRequests } from './components/AdminFeatureRequests';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'employee' | 'customer';
}

function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function checkAuth() {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;
        
        if (mounted) {
          setUser(session?.user ?? null);

          if (session?.user) {
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('role')
              .eq('id', session.user.id)
              .single();
            
            if (profileError) throw profileError;
            
            if (mounted) {
              setUserRole(profile?.role ?? null);
            }
          }
          
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Auth check error:', err);
        if (mounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    }

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (mounted) {
        setUser(session?.user ?? null);
        
        if (session?.user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', session.user.id)
            .single();
          
          if (mounted) {
            setUserRole(profile?.role ?? null);
          }
        } else {
          setUserRole(null);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center dark:bg-gray-900">
        <div className="text-red-600 dark:text-red-400">
          Error: {error}
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Allow admins to access any protected route
  if (requiredRole && userRole !== 'admin' && userRole !== requiredRole) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const notificationTimeoutRef = useRef(null);

  useOneTrust();

  useEffect(() => {
    let mounted = true;

    async function initializeAuth() {
      const { data: { session } } = await supabase.auth.getSession();
      if (mounted) {
        setUser(session?.user ?? null);
      }
    }

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (mounted) {
        setUser(session?.user ?? null);
        
        if (event === 'SIGNED_IN') {
          showNotification('Successfully signed in!');
        } else if (event === 'SIGNED_OUT') {
          showNotification('You have been signed out');
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const showNotification = (message: string) => {
    setNotification(message);
    setIsVisible(true);
    
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }

    notificationTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => setNotification(''), 300);
    }, 2500);
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Error signing out:', error);
      showNotification('Error signing out. Please try again.');
    }
  };

  const handleCookieSettings = () => {
    if (window.OneTrust?.ToggleInfoDisplay) {
      window.OneTrust.ToggleInfoDisplay();
    }
  };

  return (
    <Router>
      <RouteChangeTracker />
      
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900 transition-colors duration-200 overflow-x-hidden">
        <Navigation user={user} onLogout={handleLogout} />
        <NotificationBanner message={notification} isVisible={isVisible} />

        <main className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-2xl">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Auth />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/privacy-center" element={<PrivacyCenter />} />
            <Route path="/communication-preferences" element={<CommunicationPreferences />} />
            <Route path="/data-collection" element={<DataCollectionManagement />} />
            <Route path="/privacy-rights" element={<PrivacyRightsPage />} />
            <Route path="/cookies" element={<CookiesManagement />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/resources" element={<Resources />} />
            <Route 
              path="/feature-request" 
              element={
                <ProtectedRoute requiredRole="employee">
                  <FeatureRequest />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="feature-requests" element={<AdminFeatureRequests />} />
            </Route>
            <Route 
              path="/account" 
              element={
                <ProtectedRoute>
                  <AccountSettings />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </main>

        <Footer onCookieSettings={handleCookieSettings} />
      </div>
    </Router>
  );
}

export default App;